import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  requestAllPermissions,
  AllPermissions,
  arePermissionsGranted,
  getAllPermissionsStatus,
  subscribeToPermissionChanges,
} from '../../utils/permissions';
import { getDraftCount, getDraftsForUser, DraftProfile } from '../../services/draftStorageService';
import { getUserData, clearSession } from '../../utils/session';
import SavedDraftsDrawer from '../saved-drafts-popup/SavedDraftsDrawer';
import './welcome-message.scss';

interface WelcomeMessageProps {
  onComplete: (
    permissions: AllPermissions,
    voiceGender: 'male' | 'female',
    coordinates?: { lat: number; long: number },
    resumeDraft?: DraftProfile
  ) => void;
  onLogout?: () => void;
}

function PermissionStatusPill({ granted }: { granted: boolean }) {
  return (
    <span
      className={`permission-status-pill ${granted ? 'permission-status-pill--active' : 'permission-status-pill--inactive'}`}
      aria-label={granted ? 'فعال' : 'غير فعال'}
    >
      {granted ? '(فعال)' : '(غير فعال)'}
    </span>
  );
}

const WelcomeMessage: React.FC<WelcomeMessageProps> = ({ onComplete, onLogout }) => {
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [selectedVoice] = useState<'male' | 'female'>('male');
  const [showDraftsDrawer, setShowDraftsDrawer] = useState(false);
  const [draftCount, setDraftCount] = useState(0);
  const [savedDrafts, setSavedDrafts] = useState<DraftProfile[]>([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);

  const [permissionSnapshot, setPermissionSnapshot] = useState<AllPermissions | null>(null);

  const permissionsSectionRef = useRef<HTMLElement>(null);

  const refreshPermissions = useCallback(async () => {
    try {
      const s = await getAllPermissionsStatus();
      setPermissionSnapshot(s);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCount = async () => {
      const userData = getUserData();
      if (userData?.mobileNumber) {
        const count = await getDraftCount();
        if (!cancelled) {
          setDraftCount(count);
        }
      }
    };
    loadCount();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    refreshPermissions();
    const unsub = subscribeToPermissionChanges(() => {
      refreshPermissions();
    });
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshPermissions();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      unsub();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refreshPermissions]);

  const handleOpenDraftsDrawer = async () => {
    setIsLoadingDrafts(true);
    setShowDraftsDrawer(true);

    const userData = getUserData();
    if (userData?.mobileNumber) {
      const drafts = await getDraftsForUser(userData.mobileNumber);
      setSavedDrafts(drafts);
      setDraftCount(drafts.length);
    }

    setIsLoadingDrafts(false);
  };

  const handleDraftDeleted = async () => {
    const userData = getUserData();
    if (userData?.mobileNumber) {
      const drafts = await getDraftsForUser(userData.mobileNumber);
      setSavedDrafts(drafts);
      setDraftCount(drafts.length);
    }
  };

  const handleContinue = async (resumeDraft?: DraftProfile) => {
    setIsRequestingPermissions(true);
    setPermissionError(null);
        const currentPermissions = await getAllPermissionsStatus();

        const hasAllPermissions = arePermissionsGranted(currentPermissions);

 if (!hasAllPermissions) {
      permissionsSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    try {
      const permissions = await requestAllPermissions();
      await refreshPermissions();

      const granted = arePermissionsGranted(permissions);

      if (!granted) {
        const missing = [];
        if (!permissions.microphone.granted) missing.push('الميكروفون');
        if (!permissions.camera.granted) missing.push('الكاميرا');

        const isBlocked =
          permissions.microphone.status === 'denied' || permissions.camera.status === 'denied';

        if (isBlocked) {
          setPermissionError(
            `لقد تم رفض الوصول إلى ${missing.join(' و ')}. يرجى تفعيل الصلاحيات من إعدادات المتصفح للاستمرار.`
          );
        } else {
          setPermissionError(`يجب منح صلاحية الوصول إلى ${missing.join(' و ')} للمتابعة.`);
        }

        setIsRequestingPermissions(false);
        return;
      }

      onComplete(permissions, selectedVoice, permissions.location.coordinates, resumeDraft);
    } catch {
      setPermissionError('حدث خطأ أثناء طلب الصلاحيات. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsRequestingPermissions(false);
      await refreshPermissions();
    }
  };

  const handleSelectDraft = (draft: DraftProfile) => {
    setShowDraftsDrawer(false);
    handleContinue(draft);
  };

  const toggleDetails = () => {
    setShowDetails(prev => !prev);
  };

  useEffect(() => {
    if (!showDetails) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowDetails(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDetails]);

  const handleLogout = () => {
    clearSession();
    if (onLogout) {
      onLogout();
    } else {
      window.location.reload();
    }
  };

  const micGranted = permissionSnapshot?.microphone.granted ?? false;
  const locGranted = permissionSnapshot?.location.granted ?? false;
  const camGranted = permissionSnapshot?.camera.granted ?? false;

  const welcomeUser = getUserData();
  const displayName =
    welcomeUser?.fullName?.trim();

  return (
    <div className={`welcome-message${showDetails ? ' welcome-message--privacy-open' : ''}`}>
      <div className="">
        <header className="welcome-toolbar" dir="ltr">
          <div className="welcome-toolbar__actions">
             <button
              type="button"
              className="welcome-toolbar__icon-btn welcome-toolbar__icon-btn--danger"
              onClick={handleLogout}
              aria-label="تسجيل الخروج"
            >
              <span className="material-symbols-outlined">power_settings_new</span>
            </button> 
            <button
              type="button"
              className="welcome-toolbar__icon-btn"
              onClick={handleOpenDraftsDrawer}
              disabled={isRequestingPermissions}
              aria-label="المحادثات السابقة"
            >
              <span className="material-symbols-outlined">history</span>
              {draftCount > 0 && <span className="welcome-toolbar__badge">{draftCount}</span>}
            </button>
          
          </div>
          <img src="/new/blue.png" alt="أمين" className="welcome-toolbar__logo" />
        </header>
      <div className='welcome-content'>
        <div className="welcome-section">
          <div className="welcome-header">
            <h2 className="welcome-title">
              {displayName ? `مرحبًا بك، ${displayName}!` : 'مرحبًا بك!'}
            </h2>
            <p className="welcome-subtitle">مساعدك الذكي للمعلومات عن التأمين</p>
          </div>
        </div>

        <div className="welcome-character-card">
          <div className="welcome-character-card__inner">
            <img src="/new/Character.png" alt="" className="welcome-character-card__img" />
          </div>
        </div>

        <div className="features-overview">
          <h2 className="features-overview__title">الخدمات المقدمة</h2>
          <div className="features-grid">
            <div className="feature-tile">
              <img src="/new/motor.svg" alt="" className="feature-tile__icon" />
              <h3 className="feature-tile__title">تأمين السيارات</h3>
              <p className="feature-tile__subtitle">
                تعرف على الفرق بين الشامل وضد الغير والتحمل والعوامل المؤثرة
              </p>
            </div>
            <div className="feature-tile">
              <img src="/new/medical.svg" alt="" className="feature-tile__icon" />
              <h3 className="feature-tile__title">التأمين الطبي</h3>
              <p className="feature-tile__subtitle">
                احصل على معلومات عن شبكة المستشفيات والتغطيات والاستثناءات
              </p>
            </div>
            <div className="feature-tile">
              <img src="/new/life.svg" alt="" className="feature-tile__icon" />
              <h3 className="feature-tile__title">التأمين على الحياة</h3>
              <p className="feature-tile__subtitle">
                افهم الفرق بين التأمين المؤقت والشامل ودور المستفيدين
              </p>
            </div>
            <div className="feature-tile">
              <img src="/new/properties.svg" alt="" className="feature-tile__icon" />
              <h3 className="feature-tile__title">تأمين الممتلكات</h3>
              <p className="feature-tile__subtitle">معلومات عن تأمين الحريق والسرقة والأعمال</p>
            </div>
          </div>
        </div>

        <section className="permissions-section" ref={permissionsSectionRef} id="welcome-permissions">
          <h3 className="permissions-section__title">للبدء، نحتاج إلى</h3>
          <div className="permissions-list">
            <div className="permission-row">
              <PermissionStatusPill granted={micGranted} />
              <div className="permission-row__body">
                <img src="/new/microphone.svg" alt="" className="permission-row__icon" />
                <div className="permission-row__text">
                  <strong className="permission-row__label">الميكروفون</strong>
                  <span className="permission-row__desc">للتفاعل الصوتي</span>
                </div>
              </div>
            </div>
            <div className="permission-row">
              <PermissionStatusPill granted={locGranted} />
              <div className="permission-row__body">
                <img src="/new/location.svg" alt="" className="permission-row__icon" />
                <div className="permission-row__text">
                  <strong className="permission-row__label">الموقع</strong>
                  <span className="permission-row__desc">لتحسين تجربة المستخدم</span>
                </div>
              </div>
            </div>
            <div className="permission-row">
              <PermissionStatusPill granted={camGranted} />
              <div className="permission-row__body">
                <img src="/new/camera.svg" alt="" className="permission-row__icon" />
                <div className="permission-row__text">
                  <strong className="permission-row__label">الكاميرا</strong>
                  <span className="permission-row__desc">للميزات المحسّنة</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {permissionError && (
          <div className="permission-error">
            <p>{permissionError}</p>
          </div>
        )}

        <div className="welcome-actions">
          <button
            type="button"
            className="welcome-actions__btn welcome-actions__btn--primary"
            onClick={() => handleContinue()}
            disabled={isRequestingPermissions}
          >
            <img src="/new/chat.svg" alt="" className="welcome-actions__icon" aria-hidden />
            <span>{isRequestingPermissions ? 'الصلاحيات المطلوبة...' : 'بدأ المحادثة'}</span>
          </button>
          <button
            type="button"
            className="welcome-actions__btn welcome-actions__btn--secondary"
            onClick={toggleDetails}
            aria-expanded={showDetails}
            aria-controls="privacy-details-section"
          >
            <img src="/new/privacy.svg" alt="" className="welcome-actions__icon" aria-hidden />
            <span>{showDetails ? 'إخفاء التفاصيل' : 'تفاصيل الخصوصية'}</span>
          </button>
        </div>

        {showDetails && (
          <div className="privacy-bottom-sheet-root" role="presentation">
            <div className="privacy-bottom-sheet-backdrop" aria-hidden />
            <div
              className="privacy-bottom-sheet"
              id="privacy-details-section"
              role="dialog"
              aria-modal="true"
              aria-labelledby="privacy-details-heading"
            >
              <div className="privacy-bottom-sheet__grab" aria-hidden />
              <div className="privacy-details privacy-details--sheet">
                <h4 id="privacy-details-heading">الخصوصية والأمان</h4>
                <ul>
                  <li>يُستخدم موقعك عند الحاجة فقط ولا يتم تخزينه</li>
                  <li>يتم معالجة بث الكاميرا محلياً ولا يتم تسجيله</li>
                  <li>تتم معالجة بيانات الصوت بشكل آمن ولا يتم حفظها</li>
                  <li>جميع عمليات نقل البيانات مشفرة</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {showDraftsDrawer && (
        <SavedDraftsDrawer
          open={showDraftsDrawer}
          drafts={savedDrafts}
          isLoading={isLoadingDrafts}
          onSelectDraft={handleSelectDraft}
          onClose={() => setShowDraftsDrawer(false)}
          onDraftDeleted={handleDraftDeleted}
        />
      )}
      </div>
    </div>

  );
};

export default WelcomeMessage;
